import React, { useMemo } from 'react';
import { CardTemplate, CardTemplateField } from '@/hooks/useCardTemplates';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { getUniqueFieldsForForm, resolveCanonicalKey, isCopyField } from '@/lib/linkedFields';
import { isMrzFieldKey } from '@/lib/mrzGenerator';

interface CardBuilderFormProps {
  template: CardTemplate;
  formData: Record<string, string>;
  onFieldChange: (fieldKey: string, value: string) => void;
  onFieldFocus?: (fieldKey: string) => void;
  className?: string;
}

export const CardBuilderForm: React.FC<CardBuilderFormProps> = ({
  template,
  formData,
  onFieldChange,
  onFieldFocus,
  className,
}) => {
  // Get canonical key for this field (for linked field synchronization)
  const getCanonicalKey = (field: CardTemplateField): string => {
    return resolveCanonicalKey(template.fields || [], field.field_key);
  };

  const renderField = (field: CardTemplateField) => {
    const canonicalKey = getCanonicalKey(field);
    const value = formData[canonicalKey] || '';

    switch (field.field_type) {
      case 'select':
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={canonicalKey}>
              {field.label}
              {field.is_required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Select
              value={value}
              onValueChange={(val) => onFieldChange(canonicalKey, val)}
            >
              <SelectTrigger id={canonicalKey}>
                <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'date':
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={canonicalKey}>
              {field.label}
              {field.is_required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Input
              id={canonicalKey}
              type="text"
              placeholder={field.placeholder || 'TT.MM.JJJJ'}
              value={value}
              onChange={(e) => onFieldChange(canonicalKey, e.target.value.toUpperCase())}
              onFocus={() => onFieldFocus?.(canonicalKey)}
              maxLength={field.max_length || 20}
              className="uppercase"
            />
          </div>
        );

      default:
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={canonicalKey}>
              {field.label}
              {field.is_required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Input
              id={canonicalKey}
              type="text"
              placeholder={field.placeholder || undefined}
              value={value}
              onChange={(e) => onFieldChange(canonicalKey, e.target.value.toUpperCase())}
              onFocus={() => onFieldFocus?.(canonicalKey)}
              maxLength={field.max_length || undefined}
              className="uppercase"
            />
          </div>
        );
    }
  };

  // Fields that should be hidden from user input (auto-generated)
  const isHiddenField = (field: CardTemplateField): boolean => {
    // Hide QR code fields
    if (field.field_type === 'qrcode') return true;
    // Hide MRZ fields (auto-generated)
    if (isMrzFieldKey(field.field_key)) return true;
    return false;
  };

  // Get deduplicated fields for form display
  // This uses the canonical key logic to show only ONE field per linked group
  const uniqueFields = useMemo(() => {
    const allFields = template.fields || [];
    // First filter out hidden fields
    const visibleFields = allFields.filter(f => !isHiddenField(f));
    // Then deduplicate linked fields
    return getUniqueFieldsForForm(visibleFields);
  }, [template.fields]);

  // Group fields into rows of 2 for larger fields, keeping full-width for special cases
  // Fields are already sorted by getUniqueFieldsForForm using logical field order
  const groupFields = (fields: CardTemplateField[]) => {
    const groups: CardTemplateField[][] = [];
    let currentGroup: CardTemplateField[] = [];

    fields.forEach((field) => {
      currentGroup.push(field);
      // Full-width fields: address, institution, organization
      const isFullWidth = ['address', 'institution', 'organization', 'major'].includes(field.field_key);
      
      if (isFullWidth || currentGroup.length === 2) {
        groups.push([...currentGroup]);
        currentGroup = [];
      }
    });

    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  };

  const fieldGroups = groupFields(uniqueFields);

  return (
    <div className={cn('space-y-4', className)}>
      {fieldGroups.map((group, groupIndex) => (
        <div
          key={groupIndex}
          className={cn(
            'grid gap-4',
            group.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'
          )}
        >
          {group.map((field) => renderField(field))}
        </div>
      ))}
    </div>
  );
};
